import React from "react";
import { Users, Calendar } from "lucide-react";
import { formatDeveloperDisplay } from "@/utils/synthesisCalculations";
import { resolvePackConfig, getComparisonModel, type PackAilixConfig, type PricingMode } from "@/utils/packAilixPricing";
import { SLIDER_CONSTANTS } from "@/utils/paymentModalityCalculations";

interface TeamRoleLine {
  label: string;
  joursParMois: number;
}


/**
 * Calcule les lignes de composition d'équipe à afficher dans le résumé.
 *
 * Règles :
 * - Développeur : developers × DAYS_PER_MONTH_PER_DEV
 * - Prestations (pilotage, moe, expert_ia) : depuis packConfig
 * - per_dev : multiplie jours_par_mois par le nombre de développeurs
 * - En mode a_la_carte legacy : masquer expert_ia
 * - En mode a_la_carte hybrid : afficher expert_ia si enabled_by_default
 * - Filtrer les prestations non actives par défaut (enabled_by_default === false)
 */
function getTeamCompositionLines(
  developers: number,
  pricingMode: PricingMode,
  packConfig?: PackAilixConfig | null,
  enabledPrestations?: Record<string, boolean>,
): TeamRoleLine[] {
  const config = resolvePackConfig(packConfig);
  const comparisonModel = getComparisonModel(packConfig);
  const lines: TeamRoleLine[] = [];

  // Ligne développeur
  lines.push({
    label: "Développeur",
    joursParMois: developers * SLIDER_CONSTANTS.DAYS_PER_MONTH_PER_DEV,
  });

  // Ordre d'affichage souhaité : pilotage, moe, expert_ia
  const orderedKeys = ["pilotage", "moe", "expert_ia"];

  for (const key of orderedKeys) {
    const presta = config.prestations[key];
    if (!presta) continue;

    // En legacy : masquer expert_ia en mode a_la_carte
    // En hybrid : afficher expert_ia si activé (même en a_la_carte)
    if (key === "expert_ia" && pricingMode === "a_la_carte" && comparisonModel === "legacy") continue;

    // Admin = plafond : l'utilisateur peut désactiver mais pas activer une prestation interdite par l'admin
    if (!presta.enabled_by_default) continue;
    const isEnabled = enabledPrestations && key in enabledPrestations
      ? enabledPrestations[key]
      : true;
    if (!isEnabled) continue;

    const jours = presta.per_dev
      ? presta.jours_par_mois * developers
      : presta.jours_par_mois;

    lines.push({
      label: presta.label,
      joursParMois: jours,
    });
  }

  return lines;
}

/**
 * Formate un nombre de jours en format FR lisible.
 * Ex : 18 → "18 jours", 0.5 → "0,5 jour", 3.5 → "3,5 jours"
 */
function formatJoursParMois(jours: number): string {
  const formatted = jours.toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  return `${formatted} ${jours === 1 ? "jour" : "jours"}/mois`;
}

interface PaymentModalitySummaryProps {
  developers: number;
  projectMonths: number;
  pricingMode: PricingMode;
  packConfig?: PackAilixConfig | null;
  /** Prestations activées/désactivées (toggles Finalisation) — optionnel */
  enabledPrestations?: Record<string, boolean>;
}

export function PaymentModalitySummary({
  developers,
  projectMonths,
  pricingMode,
  packConfig,
  enabledPrestations,
}: PaymentModalitySummaryProps) {
  const roundedMonths = typeof projectMonths === "number" ? projectMonths : 0;
  const teamLines = getTeamCompositionLines(developers, pricingMode, packConfig, enabledPrestations);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="w-4 h-4" />
          Équipe
        </div>
        <div className="text-lg font-semibold">{formatDeveloperDisplay(developers)}</div>
        <div className="text-xs text-muted-foreground space-y-0.5">
          {teamLines.map((line, index) =>
            index === 0 ? (
              <div key={line.label} className="font-medium">
                {formatJoursParMois(line.joursParMois)}
              </div>
            ) : (
              <div key={line.label}>
                + 1 {line.label} ({formatJoursParMois(line.joursParMois)})
              </div>
            ),
          )}
        </div>
      </div>

      <div className="space-y-2 text-right">
        <div className="flex items-center gap-2 text-sm text-muted-foreground justify-end">
          <Calendar className="w-4 h-4" />
          Durée estimée
        </div>
        <div className="text-lg font-semibold">{roundedMonths} mois</div>
      </div>
    </div>
  );
}

export default PaymentModalitySummary;
